/**
 * BICEP UI kit — the small set of building blocks every BICEP page is made of,
 * so the module looks and behaves consistently (headers, stats, status pills,
 * forms, modals, tabs, charts, CSV export, async + error handling).
 */
import { useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Search, AlertTriangle, RefreshCw, Download, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useFetch } from "@/lib/useFetch";

export { cn };

// ── Formatting ───────────────────────────────────────────────────────────────
export const fmtDate = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  return isNaN(t) ? String(d) : t.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};
export const fmtDateTime = (d) => {
  if (!d) return "—";
  const t = new Date(d);
  return isNaN(t) ? String(d) : t.toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
};
export const fmtINR = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(v >= 1e8 ? 0 : 2).replace(/\.?0+$/, "")} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2).replace(/\.?0+$/, "")} L`;
  return `₹${v.toLocaleString("en-IN")}`;
};
export const timeAgo = (d) => {
  const t = new Date(d); if (isNaN(t)) return "";
  const s = Math.max(0, (Date.now() - t.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 30) return `${Math.floor(s / 86400)}d ago`;
  return fmtDate(d);
};
export const titleCase = (s = "") => String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
export const initials = (s = "") => String(s).split(/[\s@._-]+/).filter(Boolean).map((p) => p[0]).join("").slice(0, 2).toUpperCase() || "?";
export const safeJSON = (v, fallback = null) => { try { return typeof v === "string" ? JSON.parse(v) : (v ?? fallback); } catch { return fallback; } };
export const isUrl = (u) => /^https?:\/\//i.test(String(u || ""));
export const stageLabel = { idea: "Idea", prototype: "Prototype", mvp: "MVP", early_revenue: "Early revenue", growth: "Growth", scaling: "Scaling" };
export const STAGES = Object.keys(stageLabel);
export const DOMAINS = ["general", "tech", "business", "marketing", "legal", "design", "finance", "healthcare", "edtech", "fintech", "agritech", "other"];

// ── Tones & pills ────────────────────────────────────────────────────────────
export const TONES = {
  slate:  "bg-slate-500/10 text-slate-700 dark:text-slate-300 ring-slate-500/20",
  blue:   "bg-blue-500/10 text-blue-700 dark:text-blue-300 ring-blue-500/20",
  green:  "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-500/20",
  amber:  "bg-amber-500/12 text-amber-800 dark:text-amber-300 ring-amber-500/25",
  red:    "bg-red-500/10 text-red-700 dark:text-red-300 ring-red-500/20",
  violet: "bg-violet-500/10 text-violet-700 dark:text-violet-300 ring-violet-500/20",
  cyan:   "bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 ring-cyan-500/20",
  rose:   "bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-rose-500/20",
};
export const TONE_HEX = { slate: "#64748B", blue: "#2563EB", green: "#059669", amber: "#D97706", red: "#DC2626", violet: "#7C3AED", cyan: "#0891B2", rose: "#E11D48" };

const STATUS_TONE = {
  pending: "amber", under_review: "blue", approved: "green", rejected: "red", withdrawn: "slate", cancelled: "slate",
  assigned: "violet", in_progress: "blue", completed: "green", scheduled: "blue", rescheduled: "amber",
  planned: "slate", achieved: "green", missed: "red", applied: "amber", received: "green",
  draft: "slate", filed: "blue", objection: "amber", granted: "green", abandoned: "slate",
  registered: "blue", new: "blue", introduced: "violet", in_talks: "amber", deal_closed: "green",
  not_interested: "slate", on_hold: "slate", active: "green", paused: "amber", graduated: "violet", exited: "slate",
  submitted: "blue", reviewed: "green", open: "green", closed: "slate", true: "green", false: "slate",
};
export const statusTone = (s) => STATUS_TONE[String(s)] || "slate";

export function Pill({ tone = "slate", children, className, dot }) {
  return (
    <span className={cn("inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset whitespace-nowrap", TONES[tone], className)}>
      {dot && <span className="w-1.5 h-1.5 rounded-full" style={{ background: TONE_HEX[tone] }} />}
      {children}
    </span>
  );
}
export const StatusPill = ({ status, className }) => <Pill tone={statusTone(status)} dot className={className}>{titleCase(status || "unknown")}</Pill>;

// ── Layout ───────────────────────────────────────────────────────────────────
export function Card({ className, children, ...rest }) {
  return <div className={cn("rounded-2xl border border-border/60 bg-card text-card-foreground shadow-sm", className)} {...rest}>{children}</div>;
}

/** Page wrapper: consistent width, header, optional back link and actions. */
export function Page({ icon: Icon, title, subtitle, back, actions, tone = "blue", width = "max-w-4xl", children }) {
  return (
    <div className={cn("mx-auto w-full px-4 py-6 pb-20 space-y-5", width)}>
      <header className="flex items-start gap-3">
        {back && (
          <Button variant="ghost" size="icon" asChild className="rounded-xl bg-muted hover:bg-muted/70 shrink-0 mt-0.5" aria-label="Back">
            <Link to={back}><ArrowLeft className="w-4 h-4" /></Link>
          </Button>
        )}
        {Icon && (
          <div className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: `${TONE_HEX[tone]}1A` }}>
            <Icon className="w-5 h-5" style={{ color: TONE_HEX[tone] }} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold leading-tight tracking-tight">{title}</h1>
          {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">{actions}</div>}
      </header>
      {children}
    </div>
  );
}

export function SectionTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between mb-2.5">
      <h2 className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-muted-foreground">{children}</h2>
      {action}
    </div>
  );
}

export function StatTile({ label, value, hint, icon: Icon, tone = "blue", to }) {
  const body = (
    <Card className={cn("p-4 h-full transition-shadow", to && "hover:shadow-md")}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {Icon && <Icon className="w-4 h-4" style={{ color: TONE_HEX[tone] }} />}
      </div>
      <div className="text-2xl font-black tracking-tight tabular-nums leading-none">{value ?? "—"}</div>
      {hint && <p className="text-[11px] text-muted-foreground mt-1.5">{hint}</p>}
    </Card>
  );
  return to ? <Link to={to} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-2xl">{body}</Link> : body;
}

// ── States ───────────────────────────────────────────────────────────────────
export function EmptyBlock({ icon: Icon = Inbox, title, hint, action }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-4 rounded-2xl border border-dashed border-border/70">
      <div className="w-11 h-11 rounded-2xl bg-muted flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-muted-foreground" /></div>
      <p className="text-sm font-semibold">{title}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1 max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
export function ErrorBlock({ error, onRetry }) {
  return (
    <div role="alert" className="flex flex-col items-center text-center py-10 px-4 rounded-2xl border border-red-500/25 bg-red-500/5">
      <AlertTriangle className="w-6 h-6 text-red-500 mb-2" />
      <p className="text-sm font-semibold">Couldn't load this</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-sm">{error?.message || "Something went wrong."}</p>
      {onRetry && <Button size="sm" variant="outline" className="mt-3 gap-1.5" onClick={onRetry}><RefreshCw className="w-3.5 h-3.5" />Try again</Button>}
    </div>
  );
}
export function ListSkeleton({ rows = 4, className }) {
  return (
    <div className={cn("space-y-3", className)} aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="h-20 rounded-2xl shimmer" />)}
    </div>
  );
}

/** Loads data once and renders skeleton / error / content. `children(data, reload)`. */
export function useLoad(fetcher, deps = []) { return useFetch(fetcher, deps); }
export function Async({ state, rows = 4, children, skeleton }) {
  const { data, loading, error, reload } = state;
  if (loading && !data) return skeleton || <ListSkeleton rows={rows} />;
  if (error && !data) return <ErrorBlock error={error} onRetry={reload} />;
  return children(data || {}, reload);
}

/** Wraps a mutating call with busy state + toasts. Resolves undefined on failure. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (fn, { ok, onDone } = {}) => {
    setBusy(true);
    try {
      const r = await fn();
      if (ok) toast.success(typeof ok === "function" ? ok(r) : ok);
      onDone?.(r);
      return r ?? true;
    } catch (e) {
      toast.error(e?.message || "Something went wrong");
      return undefined;
    } finally { setBusy(false); }
  }, []);
  return { busy, run };
}

// ── Forms ────────────────────────────────────────────────────────────────────
export const inputCls = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/50 disabled:opacity-60";
export function Field({ label, hint, required, children, className }) {
  return (
    <label className={cn("block space-y-1", className)}>
      <span className="text-xs font-semibold text-foreground/80">{label}{required && <span className="text-red-500 ml-0.5" aria-hidden>*</span>}</span>
      {children}
      {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}
export const TextInput = ({ className, ...p }) => <input className={cn(inputCls, className)} {...p} />;
export const TextArea = ({ className, rows = 3, ...p }) => <textarea rows={rows} className={cn(inputCls, "resize-y", className)} {...p} />;
export function SelectInput({ className, options = [], placeholder, ...p }) {
  return (
    <select className={cn(inputCls, className)} {...p}>
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((o) => typeof o === "string"
        ? <option key={o} value={o}>{titleCase(o)}</option>
        : <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
/** Controlled form-state helper: const [f, set, reset] = useForm({...}) */
export function useForm(initial) {
  const [v, setV] = useState(initial);
  const set = (k) => (e) => setV((p) => ({ ...p, [k]: e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e }));
  const reset = (next) => setV(next ?? initial);
  return [v, set, reset, setV];
}

export function Modal({ open, onClose, title, description, children, footer, size = "md" }) {
  const width = { sm: "sm:max-w-md", md: "sm:max-w-lg", lg: "sm:max-w-2xl", xl: "sm:max-w-4xl" }[size];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className={cn("max-h-[90vh] overflow-y-auto", width)}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : <DialogDescription className="sr-only">{title}</DialogDescription>}
        </DialogHeader>
        <div className="space-y-3">{children}</div>
        {footer && <div className="flex justify-end gap-2 pt-2">{footer}</div>}
      </DialogContent>
    </Dialog>
  );
}

// ── Navigation / filtering ───────────────────────────────────────────────────
export function Tabs({ tabs, value, onChange, className }) {
  return (
    <div role="tablist" className={cn("flex gap-1 overflow-x-auto rounded-xl bg-muted p-1 scrollbar-thin", className)}>
      {tabs.map((t) => (
        <button key={t.id} role="tab" aria-selected={value === t.id} onClick={() => onChange(t.id)}
          className={cn("shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors",
            value === t.id ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")}>
          {t.label}{t.count != null && <span className="ml-1.5 text-[10px] opacity-70 tabular-nums">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}
export function Chips({ options, value, onChange, className }) {
  return (
    <div className={cn("flex flex-wrap gap-1.5", className)}>
      {options.map((o) => {
        const v = typeof o === "string" ? o : o.value, l = typeof o === "string" ? titleCase(o) : o.label;
        return (
          <button key={v} onClick={() => onChange(v)} aria-pressed={value === v}
            className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors",
              value === v ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:text-foreground")}>
            {l}{typeof o === "object" && o.count != null && <span className="ml-1 opacity-70 tabular-nums">{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}
export function SearchBox({ value, onChange, placeholder = "Search…", className }) {
  return (
    <div className={cn("relative", className)}>
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <input className={cn(inputCls, "pl-9")} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} aria-label={placeholder} />
    </div>
  );
}
/** Case-insensitive match of `q` against the given keys of each row. */
export function useFilter(rows, q, keys) {
  return useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return rows;
    return rows.filter((r) => keys.some((k) => String(r[k] ?? "").toLowerCase().includes(n)));
  }, [rows, q, keys]); // eslint-disable-line
}

// ── Charts (dependency-free) ─────────────────────────────────────────────────
export function BarList({ data, tone = "blue", empty = "No data yet", format = (n) => n, labels = {} }) {
  const entries = Object.entries(data || {}).filter(([, v]) => Number(v) > 0).sort((a, b) => b[1] - a[1]);
  if (!entries.length) return <p className="text-xs text-muted-foreground py-3">{empty}</p>;
  const max = Math.max(...entries.map(([, v]) => v));
  return (
    <ul className="space-y-2">
      {entries.map(([k, v]) => (
        <li key={k}>
          <div className="flex justify-between text-xs mb-1"><span className="font-medium">{labels[k] || titleCase(k)}</span><span className="tabular-nums text-muted-foreground">{format(v)}</span></div>
          <div className="h-2 rounded-full bg-muted overflow-hidden" role="presentation">
            <div className="h-full rounded-full" style={{ width: `${(v / max) * 100}%`, background: TONE_HEX[statusTone(k) !== "slate" ? statusTone(k) : tone] }} />
          </div>
        </li>
      ))}
    </ul>
  );
}
export function ColumnChart({ data, tone = "blue", height = 130, empty = "No data yet", format = (n) => n }) {
  if (!data?.length) return <p className="text-xs text-muted-foreground py-3">{empty}</p>;
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="flex items-end justify-start gap-2" style={{ height }} role="img" aria-label="Column chart">
      {data.map((d) => (
        <div key={d.month} className="flex-1 max-w-[64px] flex flex-col items-center justify-end h-full min-w-0" title={`${d.month}: ${format(d.count)}`}>
          <span className="text-[10px] tabular-nums text-muted-foreground mb-0.5 whitespace-nowrap">{format(d.count)}</span>
          <div className="w-full rounded-t-md min-h-[3px]" style={{ height: `${(d.count / max) * 75}%`, background: TONE_HEX[tone] }} />
          <span className="text-[9px] text-muted-foreground mt-1 truncate w-full text-center">{d.month}</span>
        </div>
      ))}
    </div>
  );
}
export function ProgressBar({ value, tone = "green", label }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div role="progressbar" aria-valuenow={v} aria-valuemin={0} aria-valuemax={100} aria-label={label}>
      <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${v}%`, background: TONE_HEX[tone] }} /></div>
    </div>
  );
}

// ── CSV export ───────────────────────────────────────────────────────────────
/** columns: [{ key, label, get? }]. Cells beginning with = + - @ are prefixed to defuse spreadsheet formula injection. */
export function downloadCSV(filename, rows, columns) {
  const esc = (v) => {
    let s = v == null ? "" : String(v);
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(c.get ? c.get(r) : r[c.key])).join(",")).join("\n");
  const blob = new Blob(["﻿" + head + "\n" + body], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}
export function ExportButton({ filename, rows, columns, disabled }) {
  return (
    <Button size="sm" variant="outline" className="gap-1.5" disabled={disabled || !rows?.length}
      onClick={() => downloadCSV(`${filename}-${new Date().toISOString().slice(0, 10)}.csv`, rows, columns)}>
      <Download className="w-3.5 h-3.5" />Export CSV
    </Button>
  );
}

/** A labelled key/value row used in detail views. */
export function Detail({ label, children }) {
  if (children == null || children === "") return null;
  return (
    <div className="grid grid-cols-[110px_1fr] gap-2 text-sm py-1.5 border-b border-border/40 last:border-0">
      <dt className="text-xs text-muted-foreground pt-0.5">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}
